import React from 'react';
import LegalDocumentLayout from './LegalDocumentLayout';

export default function TermsPage() {
  return (
    <LegalDocumentLayout title="Terms of Service" effectiveDate="April 2, 2026">
      <h2>1. Agreement to Terms</h2>
      <p>
        These Terms of Service (&quot;Terms&quot;) govern your access to and use of the websites, applications,
        and services offered by Sovrynn LLC (&quot;Sovrynn,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;)
        (collectively, the &quot;Services&quot;). By creating an account, accessing, or using the Services, you
        agree to be bound by these Terms. If you do not agree, do not use the Services.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        You must be at least 18 years old and able to form a binding contract in your jurisdiction to use the
        Services. If you use the Services on behalf of a business, you represent that you have authority to
        bind that business to these Terms.
      </p>

      <h2>3. Accounts and Security</h2>
      <p>
        You are responsible for maintaining the confidentiality of your account credentials and for all activity
        under your account. You agree to provide accurate registration information and to notify us promptly
        at support@sovrynn.co of any unauthorized use or security breach.
      </p>

      <h2>4. Description of Services</h2>
      <p>
        Sovrynn provides software tools and informational content related to motor carrier compliance,
        documentation, and related workflows. The Services may include dashboards, document storage,
        notifications, third-party integrations, and an AI-assisted assistant (&quot;Rynn&quot;). Features may
        change over time. We do not guarantee uninterrupted or error-free operation.
      </p>

      <h2>5. Not Legal or Professional Advice</h2>
      <p>
        The Services and any outputs (including from Rynn) are for informational and organizational purposes
        only. They do not constitute legal, tax, insurance, safety, or regulatory advice. FMCSA and state
        requirements change; you are solely responsible for your compliance decisions. Consult qualified
        professionals and official government sources when needed.
      </p>

      <h2>6. User Content and License</h2>
      <p>
        You retain ownership of data and files you submit (&quot;User Content&quot;). You grant Sovrynn a
        non-exclusive, worldwide license to host, process, display, and use User Content solely to provide,
        secure, and improve the Services and as described in our Privacy Policy. You represent that you have
        the rights necessary to grant this license.
      </p>

      <h2>7. Acceptable Use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Violate applicable law or third-party rights</li>
        <li>Attempt to gain unauthorized access to the Services, systems, or other users&apos; data</li>
        <li>Use the Services to transmit malware, spam, or deceptive content</li>
        <li>Reverse engineer, scrape, or overload the Services except as permitted by law</li>
        <li>Use the Services in a manner that could harm Sovrynn, users, or the public</li>
      </ul>

      <h2>8. Subscriptions and Fees</h2>
      <p>
        Paid plans, billing cycles, and taxes are disclosed at checkout or in your account. Unless stated
        otherwise, fees are non-refundable except as required by law. We may change pricing with reasonable
        notice where required. Failure to pay may result in suspension or termination of access.
      </p>

      <h2>9. Intellectual Property</h2>
      <p>
        Sovrynn and its licensors own all rights in the Services, software, branding, and related materials,
        excluding your User Content. No rights are granted except the limited license to use the Services in
        accordance with these Terms.
      </p>

      <h2>10. Termination</h2>
      <p>
        You may stop using the Services at any time. We may suspend or terminate access if you violate these
        Terms, if required by law, or to protect the Services or users. Provisions that by their nature should
        survive (including disclaimers, limitations of liability, and dispute resolution) will survive
        termination.
      </p>

      <div className="legal-page__caps-card">
        <h2>11. DISCLAIMER OF WARRANTIES</h2>
        <p>
          THE SERVICES ARE PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY
          KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY,
          FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT. SOVRYNN DOES NOT WARRANT THAT THE
          SERVICES WILL BE ACCURATE, COMPLETE, RELIABLE, SECURE, OR FREE OF ERRORS OR HARMFUL COMPONENTS.
        </p>

        <h2>12. LIMITATION OF LIABILITY</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL SOVRYNN LLC, ITS AFFILIATES,
          OFFICERS, DIRECTORS, EMPLOYEES, OR SUPPLIERS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
          CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, GOODWILL, OR BUSINESS
          OPPORTUNITIES, ARISING OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THE SERVICES, EVEN IF
          SOVRYNN HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
        </p>
        <p>
          SOVRYNN&apos;S TOTAL LIABILITY FOR ANY CLAIM ARISING OUT OF OR RELATING TO THESE TERMS OR THE
          SERVICES SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU PAID TO SOVRYNN FOR THE SERVICES IN THE
          TWELVE (12) MONTHS BEFORE THE CLAIM OR (B) ONE HUNDRED U.S. DOLLARS (US $100), EXCEPT WHERE PROHIBITED
          BY LAW.
        </p>
        <p>
          SOME JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS; IN THOSE CASES, SOVRYNN&apos;S LIABILITY IS
          LIMITED TO THE MAXIMUM EXTENT PERMITTED BY LAW.
        </p>
      </div>

      <h2>13. Indemnity</h2>
      <p>
        You will defend, indemnify, and hold harmless Sovrynn and its affiliates from claims, damages, losses,
        and expenses (including reasonable attorneys&apos; fees) arising from your User Content, your use of
        the Services, or your violation of these Terms or applicable law, except to the extent caused by
        Sovrynn&apos;s gross negligence or willful misconduct.
      </p>

      <h2>14. Governing Law and Disputes</h2>
      <p>
        These Terms are governed by the laws of the State of Delaware, USA, excluding conflict-of-law rules,
        unless a different governing law is required by your jurisdiction. You and Sovrynn agree to submit to
        the exclusive jurisdiction of the state and federal courts located in Delaware for disputes not subject
        to arbitration, except that either party may seek injunctive relief in any court of competent
        jurisdiction.
      </p>

      <h2>15. Changes</h2>
      <p>
        We may modify these Terms from time to time. We will post the updated Terms and update the effective
        date. Material changes may require additional notice as required by law. Continued use after changes
        become effective constitutes acceptance of the revised Terms.
      </p>

      <h2>16. SMS Messages</h2>
      <p>
        Optional SMS from Rynn is delivered by Twilio. Messages and photos sent by text are processed by
        an AI service provider to read documents and answer questions about your own account. We do not
        sell phone numbers or text messages, and we do not share SMS opt-in data with third parties for
        marketing. You can opt out at any time by replying STOP. Consent to SMS is not a condition of
        purchase.
      </p>

      <h2>17. Contact</h2>
      <p>
        For questions about these Terms, contact{' '}
        <a href="mailto:support@sovrynn.co">support@sovrynn.co</a>.
      </p>
    </LegalDocumentLayout>
  );
}
